import { componentGroupCls } from "@faire/slate/lib/tracking";
import type { IFilterGroupWebProps } from "@faire/slate/private/filter/types";
import { OverflowLayout } from "@faire/slate/private/layout/OverflowLayout";
import type { OverflowVariant } from "@faire/slate/private/shared/types";
import React from "react";

import { FilterButton } from "./FilterButton";
import { FilterToggle } from "./FilterToggle";

type IFilterButtonProps = React.ComponentProps<typeof FilterButton>;
type IFilterToggleProps = React.ComponentProps<typeof FilterToggle>;

type IProps = {
  /**
   * The filter button props. When provided, a filter button is rendered as the first item in the group.
   */
  filterButtonProps?: IFilterButtonProps;
  /**
   * Controls the overflow behavior of the filter group.
   *
   * @default "scroll"
   */
  overflow?: OverflowVariant;

  /**
   * **web**
   * The filter items (FilterToggle props).
   */
  items: IFilterToggleProps[];

  // DO NOT ADD any new prop without aligning with Slate team first
} & IFilterGroupWebProps;

/**
 * A group composed of an optional Filter Button and a list of Filter Toggles.
 *
 * Filter Button is intended to be used as a button that performs an action when clicked.
 *
 * Filter Toggle is intended to be used as a toggle that changes state when clicked.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Filter-2242efb5c25a80b8adb5c588806269bf | Slate/FilterGroup}
 */
export const FilterGroup: React.FC<IProps> = ({
  filterButtonProps,
  items,
  overflow = "scroll",
  ...props
}) => (
  <OverflowLayout
    {...props}
    className={componentGroupCls("fs-filter-group gap-2")}
    data-component-name="Slate/FilterGroup"
    overflow={overflow}
    role="group"
  >
    {filterButtonProps ? <FilterButton {...filterButtonProps} /> : null}
    {items.map((item) => (
      <FilterToggle key={item.label} {...item} />
    ))}
  </OverflowLayout>
);
